<script setup lang="ts">
import { ref, watch } from 'vue';
import AutoComplete, { AutoCompleteCompleteEvent } from 'primevue/autocomplete';
import type { AccountTypes, AssociationAccount } from '@apparatix/types/Leads';
import { useAccounts, useOpportunities, useStringFunctions, useUtilities } from '@apparatix/composables';
import { newAccountSchema, transformSearchResult, type PendingAccount } from '@apparatix/schemas/Account';
import { createObjectFromSchema } from '@apparatix/schemas/Helpers';


type Props = {
  forceSelection?: boolean;
  disabled?: boolean;
  placeholder?: string;
  invalid?: boolean;
  agencyId?: number;
  searchDelay?: number;
  minQueryLength?: number;
  disableOption?: (account: AssociationAccount) => boolean;
  excludeTypes?: AccountTypes[];
  isOpportunityWizard?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: 'Search or enter name',
  agencyId: undefined,
  searchDelay: 300,
  minQueryLength: 3,
  disableOption: undefined,
  excludeTypes: () => [],
  isOpportunityWizard: false,
});

const accountModel = defineModel<PendingAccount>();

const { isEmptyString, formatAddress } = useStringFunctions();
const { searchAccounts, getAdvertisersByAgency } = useAccounts();
const { arrayHasValues } = useUtilities();
const { findUserById } = useOpportunities();

const userInput = ref<AssociationAccount | string>();
const suggestions = ref<AssociationAccount[]>([]);
const advertisersAssociatedWithAgency = ref<AssociationAccount[]>([]);

watch(() => userInput.value, () => {
  if (typeof userInput.value === 'object') {
    accountModel.value = transformSearchResult(userInput.value, findUserById(userInput.value.ownerId));
  }

  if (typeof userInput.value === 'string' && !isEmptyString(userInput.value, true)) {
    accountModel.value = {
      name: userInput.value,
    };
  }

  if (!userInput.value || (typeof userInput.value === 'string' && isEmptyString(userInput.value, true))) {
    accountModel.value = createObjectFromSchema(newAccountSchema);
  }
});

watch(
  () => props.agencyId,
  (agencyId) => {
    if (!agencyId) {
      advertisersAssociatedWithAgency.value = [];
      return;
    }

    getAdvertisersByAgency(agencyId, results => {
      if (arrayHasValues(results)) {
        advertisersAssociatedWithAgency.value = results;
      } else {
        advertisersAssociatedWithAgency.value = [];
      }
    });
  },
  { immediate: true },
);

const shouldDisableOption = (account: AssociationAccount) => {
  if (account.c2Type === 'Unqualified Advertiser' && props.isOpportunityWizard) return true;
  if (!account.isAllowedSelection) return true;
  if (typeof props.disableOption === 'function') {
    return props.disableOption(account);
  }

  return false;
};

const search = (e: AutoCompleteCompleteEvent) => {
  if (props.agencyId) {
    const matches = advertisersAssociatedWithAgency.value.filter(account => account.c2Name?.toLowerCase()?.includes(e.query.toLowerCase()));
    if (arrayHasValues(matches)) {
      suggestions.value = matches;
    } else {
      suggestions.value = [];
    }
  } else {
    searchAccounts({ name: e.query }, results => {
      if (arrayHasValues(results)) {
        if (arrayHasValues(props.excludeTypes)) {
          suggestions.value = results.filter(account => props.excludeTypes.includes(account.c2RawType));
        } else {
          suggestions.value = results;
        }
      } else {
        suggestions.value = [];
      }
    });
  }
};

const reset = () => {
  userInput.value = undefined;
  suggestions.value = [];
};

defineExpose({ reset });
</script>
<template>
  <AutoComplete
    v-model="userInput"
    option-label="c2Name"
    :placeholder="props.placeholder"
    :delay="props.searchDelay"
    :min-length="props.minQueryLength"
    :disabled="props.disabled"
    :invalid="props.invalid"
    :show-empty-message="false"
    :option-disabled="shouldDisableOption"
    :suggestions="suggestions"
    fluid
    @complete="search"
    @update:model-value="val => userInput = val"
  >
    <template #header>
      <div class="flex items-center bg-[var(--account-search-bg)] p-2">
        <p v-if="!props.agencyId" class="text-xs text-[var(--account-search-color)] font-semibold uppercase ml-3 py-2">
          Existing Accounts with similar names
        </p>
        <p v-else class="text-xs text-[var(--account-search-color)] font-semibold uppercase ml-3 py-2">
          Suggested Advertisers
        </p>
      </div>
    </template>
    <template #option="slotProps">
      <div class="flex items-center ml-3 gap-3 text-[var(--account-search-color)]">
        <span class="pi pi-building" aria-label="Building icon" />
        <div class="flex flex-col gap-1">
          <p class="text-sm font-medium leading-none">
            {{ `${slotProps.option.c2Name}${slotProps.option.c2Type === 'Agency' ? ' (Agency)' : ''}` }}
          </p>
          <p v-if="slotProps.option.addresses?.length > 0" class="text-xs font-normal leading-none">
            {{ formatAddress(slotProps.option.addresses[0]) }}
          </p>
        </div>
      </div>
    </template>
    <template #empty>
      <div />
    </template>
  </AutoComplete>
</template>
